<template>
  <form-wrapper>
    <b-form @submit="submit">
      <h2 class="text-center">
        Create a new component
      </h2>
      <b-form-group>
        <label for="name-input">Name</label>
        <b-form-input
          v-validate="'required'"
          id="name-input"
          :disabled="saving"
          :class="{ 'is-invalid' : errors.has('name') || apiErrors.name.length }"
          :value="fields.name"
          class="form-control"
          type="text"
          name="name"
          @input="setName"/>
        <validation-errors :validation-errors="[errors.first('name')]"/>
        <validation-errors v-if="!errors.has('name')" :validation-errors="apiErrors.name"/>
      </b-form-group>

      <b-form-group>
        <label for="description-input">Description</label>
        <b-form-textarea
          v-validate="'required'"
          id="description-input"
          :disabled="saving"
          :class="{ 'is-invalid' : errors.has('description') || apiErrors.description.length }"
          :rows="3"
          :max-rows="6"
          :value="fields.description"
          type="text"
          name="description"
          @input="setDescription"/>
        <validation-errors :validation-errors="[errors.first('description')]"/>
        <validation-errors v-if="!errors.has('description')" :validation-errors="apiErrors.description"/>
      </b-form-group>

      <b-form-group>
        <b-form-select
          id="image-selection"
          :options="imageOptions"
          :disabled="saving"
          :class="{ 'is-invalid' : errors.has('preference') }"
          v-model="imageSelection"
          name="preference"
          @change="imageSelect"/>
        <validation-errors :validation-errors="[errors.first('preference')]"/>
      </b-form-group>

      <span v-if="imageSelection === 'upload'">
        <b-form-group>
          <image-upload 
            ref="imageUploadRef"
            @fileForEdit="setImage"
            @fileName="setImageName"
            @cropImage="setCropImage"
            @imageUploadHasError="setImageUploadHasError"
          />
        </b-form-group>

        <b-form-group>
          <label for="image-name-input">Image Name</label>
          <b-form-input
            v-validate="'required'"
            id="image-name-input"
            :disabled="saving"
            :class="{ 'is-invalid' : errors.has('imageName') || apiErrors.imageName.length }"
            :value="fields.imageName"
            class="form-control"
            type="text"
            data-vv-as="image name"
            name="imageName"
            @input="setImageName"/>
          <validation-errors :validation-errors="[errors.first('imageName')]"/>
          <validation-errors v-if="!errors.has('imageName')" :validation-errors="apiErrors.imageName"/>
        </b-form-group>
      </span>

      <b-form-group v-if="imageSelection === 'library'">
        <b-button :disabled="saving" variant="outline-secondary" @click="showMediaLibrary = true">
          Browse Library
        </b-button>
      </b-form-group>

      <b-form-group v-if="selectedImage && imageSelection === 'library'">
        <b-row>
          <b-col cols="12" lg="7">
            <label for="preview">Selected Image</label>
            <image-preview
              :src="selectedImageSrc"
              @click.native="displayImage(true)"/>
          </b-col>
          <b-col cols="12" lg="5" class="mt-lg-4 mt-2">
            <span class="name">Name:</span>
            {{ selectedImage.name }} <br>
            <span class="name">Size:</span>
            {{ readableFilesize(selectedImage.size) }} <br>
            <span class="name">Created:</span>
            {{ readableDate(selectedImage.created) }}
          </b-col>
        </b-row>
      </b-form-group>

      <b-form-group class="form-buttons">
        <b-button :disabled="disabled" type="submit" variant="primary">
          Confirm
          <span v-if="saving"><i class="fas fa-spinner fa-spin"></i></span>
        </b-button>
      </b-form-group>

      <form-fail :form-errors="apiErrors.other"/>

      <form-success :success="success" class="form-alert">
        Component Created!
      </form-success>
    </b-form>

    <b-modal
      v-model="showMediaLibrary"
      title="Media Library"
      centered hide-footer
      size="lg">
      <media-table
        v-if="showMediaLibrary"
        :view="mediaView"
        :show-type-select="false"
        :show-module-select="false"
        context="selectOne"
        type="image"
        @selectMedia="setImageID"/>
    </b-modal>

  </form-wrapper>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { readableDate, readableFilesize } from '../../../../helpers'
import formSuccess from '../../../common/form/form-success'
import formFail from '../../../common/form/form-fail'
import formWrapper from '../../../common/form/form-wrapper'
import imagePreview from '../../../common/image-preview'
import imageUpload from '../../../common/form/image-upload'
import mediaTable from '../media/table'
import validationErrors from '../../../common/form/validation-errors'

export default {
  components: {
    formSuccess,
    formFail,
    formWrapper,
    imagePreview,
    imageUpload,
    mediaTable,
    validationErrors
  },
  data () {
    return {
      imageSelection: 'upload',
      imageOptions: [
        { text: 'Main Image', value: null },
        { text: 'Upload a new image', value: 'upload' },
        { text: 'Choose an image from the library', value: 'library' }
      ],
      showMediaLibrary: false,
      showUpload: false,
      selectedImage: null,
      imageUploadHasError: false,
      showMediaModal: false
    }
  },
  computed: {
    ...mapState('components/forms/store', [
      'apiErrors',
      'fields',
      'success',
      'saving'
    ]),
    ...mapState('components', {
      component: 'current'
    }),
    ...mapState('config', {
      apiConfig: 'data'
    }),
    ...mapState('media', {
      mediaView: '_defaultView'
    }),
    disabled () {
      return this.errors.any() || this.saving || this.imageUploadHasError
    },
    selectedImageSrc () {
      if (this.selectedImage && this.selectedImage.thumbnail) {
        return this.selectedImage.thumbnail.url
      } else if (this.selectedImage) {
        return this.selectedImage.url
      } else {
        return ''
      }
    }
  },
  watch: {
    success (newValue, oldValue) {
      if (newValue === true && oldValue === false) {
        this.resetFields()
        this.$refs.imageUploadRef.reset()
      }
    }
  },
  methods: {
    readableFilesize,
    readableDate,
    ...mapActions({
      storeComponent: 'components/store'
    }),
    submit (event) {
      event.preventDefault()
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.storeComponent()
        }
      })
    },
    setName (event) {
      this.$store.commit('components/forms/store/setField', {
        field: 'name',
        value: event
      })
    },
    setDescription (event) {
      this.$store.commit('components/forms/store/setField', {
        field: 'description',
        value: event
      })
    },
    setImage (image) {
      let imageName = null
      let first = true
      if (this.fields.image) {
        imageName = this.fields.image.name
        first = false
      }

      this.$store.commit('components/forms/store/setField', {
        field: 'image',
        value: image
      })

      const updatedImageName = this.fields.image.name
      const changed = imageName !== updatedImageName

      if (
        this.fields.imageName === '' ||
        this.fields.imageName === null ||
        !first && changed
      ) {
        this.$store.commit('components/forms/store/setField', {
          field: 'imageName',
          value: updatedImageName.replace(/\.[^/.]+$/, '')
        })
      }
    },
    setImageName (event) {
      this.$store.commit('components/forms/store/setField', {
        field: 'imageName',
        value: event
      })
    },
    setImageID (event) {
      this.selectedImage = event
      this.$store.commit('components/forms/store/setField', {
        field: 'imageID',
        value: event.id
      })
      this.showMediaLibrary = false
    },
    setCropImage (value) {
      this.$store.commit('media/forms/store/setField', {
        field: 'crop_image',
        value: value
      })
    },
    reset (event) {
      this.$refs.imageUploadRef.reset()
      this.errors.clear()
      this.imageSelection = null
      this.selectedImage = null
    },
    resetFields (event) {
      this.reset()
      this.$store.commit('components/forms/store/resetFields')
    },
    resetForm (event) {
      this.reset()
      this.$store.commit('components/forms/store/resetState')
    },
    imageSelect (event) {
      if (event === 'library') {
        this.showMediaLibrary = true
      } 
    },
    displayImage (show) {
      this.showMediaModal = show
    },
    setImageUploadHasError(hasError) {
      this.imageUploadHasError = hasError
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/admin/components/add.vue